import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CRM_PIPELINE_STATUSES } from '@bcc/shared';
import type { Lead, LeadSource } from '@bcc/shared';
import { SITE_FORM_CONTROL, SITE_GLASS_TILE } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const STAGES = Object.values(CRM_PIPELINE_STATUSES);

const SOURCE_LABEL: Record<LeadSource, string> = {
  ira_401k: 'IRA / 401(k)',
  contact: 'Contact',
  generic: 'Generic',
  event_rsvp: 'Event RSVP',
  book_401k: 'Book (401k)',
};

/** Kanban for marketing captures — same lanes as CRM pipeline board. */
export function AdminLeadsPipeline() {
  const { session } = useAuth();
  const token = session?.access_token;
  const [leads, setLeads] = useState<Lead[]>([]);
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 10 } }));

  useEffect(() => {
    if (!token) return;
    void (async () => {
      const r = await apiFetch<{ leads: Lead[] }>('/api/staff/leads', { token });
      setLeads(r.leads);
    })();
  }, [token]);

  const filtered = useMemo(() => {
    if (sourceFilter === 'all') return leads;
    return leads.filter((l) => l.source === sourceFilter);
  }, [leads, sourceFilter]);

  const grouped = useMemo(() => {
    const bucket: Record<string, Lead[]> = {};
    STAGES.forEach((s) => {
      bucket[s] = [];
    });
    for (const lead of filtered) {
      const lane = lead.pipeline_status;
      if (!bucket[lane]) bucket[lane] = [];
      bucket[lane].push(lead);
    }
    return bucket;
  }, [filtered]);

  async function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || !token) return;
    const leadId = String(active.id);
    const maybeStage = String(over.id);
    if (!(STAGES as readonly string[]).includes(maybeStage)) return;
    const current = leads.find((x) => x.id === leadId);
    if (!current || current.pipeline_status === maybeStage) return;

    await apiFetch(`/api/staff/leads/${leadId}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ pipeline_status: maybeStage }),
    });
    setLeads((rows) =>
      rows.map((r) => (r.id === leadId ? { ...r, pipeline_status: maybeStage as Lead['pipeline_status'] } : r)),
    );
  }

  return (
    <div className="space-y-8 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-3 text-sm">
            <Link to="/admin/leads" className="text-[var(--home-muted-on-dark)] hover:text-[var(--home-miami-blue)]">
              ← Leads table
            </Link>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-[var(--home-text-on-dark)]">Lead pipeline</h1>
          <p className="mt-2 max-w-xl text-sm text-[var(--home-muted-on-dark)]">
            Drag captures across the same stages as the CRM board — new through converted or lost. Updates sync to
            Supabase.
          </p>
        </div>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className={SITE_FORM_CONTROL}
        >
          <option value="all">All sources</option>
          <option value="ira_401k">{SOURCE_LABEL.ira_401k}</option>
          <option value="contact">{SOURCE_LABEL.contact}</option>
          <option value="generic">{SOURCE_LABEL.generic}</option>
          <option value="event_rsvp">{SOURCE_LABEL.event_rsvp}</option>
          <option value="book_401k">{SOURCE_LABEL.book_401k}</option>
        </select>
      </div>

      <DndContext collisionDetection={closestCorners} sensors={sensors} onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          {STAGES.map((stage) => (
            <LeadKanbanLane key={stage} stage={stage}>
              {(grouped[stage] ?? []).map((lead) => (
                <LeadKanbanCard key={lead.id} lead={lead} />
              ))}
            </LeadKanbanLane>
          ))}
        </div>
      </DndContext>
    </div>
  );
}

function LeadKanbanLane({ stage, children }: { stage: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  return (
    <div
      ref={setNodeRef}
      className={`min-h-[360px] p-3 shadow-[0_18px_50px_-30px_rgb(0_0_0/0.5)] transition-colors ${
        isOver
          ? `${SITE_GLASS_TILE} border-[var(--home-miami-blue)]/65 bg-[var(--home-zone-dark)]/60 ring-1 ring-[var(--home-miami-blue)]/35`
          : SITE_GLASS_TILE
      }`}
    >
      <div className="mb-4 text-[11px] font-semibold uppercase tracking-wide text-[var(--home-muted-on-dark)]">
        {stage}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function LeadKanbanCard({ lead }: { lead: Lead }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: lead.id });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <div ref={setNodeRef} {...listeners} {...attributes} style={style}>
      <div className="cursor-grab rounded-xl border border-white/[0.1] bg-white/[0.06] p-3 shadow-inner shadow-black/25 backdrop-blur-md transition-colors hover:border-[var(--home-miami-blue)]/45 active:cursor-grabbing">
        <p className="text-sm font-medium leading-snug text-[var(--home-text-on-dark)]">{lead.full_name ?? '—'}</p>
        <p className="mt-1 text-[11px] text-[var(--home-muted-on-dark)]">{lead.email}</p>
        <div className="mt-3 flex justify-between gap-3 text-[11px] uppercase text-emerald-200/85">
          <span className="truncate">{SOURCE_LABEL[lead.source] ?? lead.source}</span>
        </div>
        <Link
          to={`/admin/leads#lead-${lead.id}`}
          className="mt-3 inline-block text-[11px] font-medium text-[var(--home-miami-blue)] hover:brightness-110"
          onClick={(evt) => evt.stopPropagation()}
        >
          open in table →
        </Link>
      </div>
    </div>
  );
}
